import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Chip,
  alpha,
  Paper
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SyncIcon from '@mui/icons-material/Sync';
import PaletteIcon from '@mui/icons-material/Palette';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import RouteIcon from '@mui/icons-material/Route';
import GridViewIcon from '@mui/icons-material/GridView';
import NavigationIcon from '@mui/icons-material/Navigation';
import { useLandingPageAppearance } from '../../context/LandingPageAppearanceContext';
import PortalTemplatePickerGrid from './PortalTemplatePickerGrid';
import { LOGIN_PORTAL_THEMES } from '../../theme/loginPortalThemes';

const TAB_W = 32;
const EXPANDED_PANEL_W = 368;

function ThemeDetailTable({ themeId }) {
  const th = LOGIN_PORTAL_THEMES[themeId];
  if (!th) return null;
  const rows = [
    { label: 'Page gradient', value: th.background },
    { label: 'Panel background', value: th.panelBg },
    { label: 'Panel border', value: th.panelBorder },
    { label: 'Primary text', value: th.textPrimary },
    { label: 'Secondary text', value: th.textSecondary },
    { label: 'Card surface', value: th.cardBg },
    { label: 'Decor accent 1', value: th.decorColor1 },
    { label: 'Decor accent 2', value: th.decorColor2 }
  ];
  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.5,
        borderRadius: 1.5,
        bgcolor: alpha('#000', 0.04),
        border: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1 }}>
        Token preview — {th.label}
      </Typography>
      <Stack spacing={0.75}>
        {rows.map((row) => (
          <Stack key={row.label} direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 28,
                height: 20,
                borderRadius: 0.5,
                flexShrink: 0,
                background: row.value,
                border: '1px solid',
                borderColor: alpha('#000', 0.12)
              }}
            />
            <Typography variant="caption" sx={{ minWidth: 88, fontWeight: 600, color: 'text.secondary' }}>
              {row.label}
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontFamily: 'monospace', fontSize: '0.62rem', color: 'text.primary', wordBreak: 'break-all' }}
            >
              {row.value}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

function SelectorSection({ icon, title, subtitle, themeId, onSelect, defaultExpanded = false }) {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '12px !important',
        mb: 1.5,
        '&:before': { display: 'none' },
        overflow: 'hidden',
        bgcolor: 'background.paper'
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5, minHeight: 52 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ pr: 0.5, minWidth: 0 }}>
          <Box sx={{ color: 'primary.main', display: 'flex', flexShrink: 0 }}>{icon}</Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
              {subtitle}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={LOGIN_PORTAL_THEMES[themeId]?.label || themeId}
            sx={{ fontWeight: 600, height: 22, flexShrink: 0 }}
          />
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
        <PortalTemplatePickerGrid selectedId={themeId} onSelect={onSelect} showDetails columns="auto" />
        <ThemeDetailTable themeId={themeId} />
      </AccordionDetails>
    </Accordion>
  );
}

export default function LandingTemplateSelector() {
  const {
    pageTemplateId,
    setPageTemplateId,
    panelsTemplateId,
    setPanelsTemplateId,
    careerTemplateId,
    setCareerTemplateId,
    featuresTemplateId,
    setFeaturesTemplateId,
    navVariant,
    setNavVariant,
    syncToQueryPortal,
    resetAll
  } = useLandingPageAppearance();

  const [expanded, setExpanded] = useState(false);

  return (
    <Box
      sx={{
        position: 'fixed',
        left: 0,
        top: { xs: 68, sm: 76 },
        zIndex: 1090,
        display: 'flex',
        alignItems: 'flex-start',
        maxWidth: '100vw',
        pointerEvents: 'none',
        '& > *': { pointerEvents: 'auto' }
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-label={expanded ? 'Collapse template chooser' : 'Expand template chooser'}
        sx={{
          width: TAB_W,
          flexShrink: 0,
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          py: 1,
          px: 0.25,
          bgcolor: 'primary.main',
          color: '#fff',
          borderRadius: '0 8px 8px 0',
          boxShadow: 2,
          '&:hover': { bgcolor: 'primary.dark' }
        }}
      >
        {expanded ? <ChevronLeftIcon sx={{ fontSize: 18 }} /> : <ChevronRightIcon sx={{ fontSize: 18 }} />}
        <Typography
          variant="caption"
          sx={{
            writingMode: 'vertical-rl',
            transform: 'rotate(180deg)',
            fontWeight: 800,
            letterSpacing: 0.12,
            fontSize: '0.62rem',
            lineHeight: 1.15,
            userSelect: 'none',
            whiteSpace: 'nowrap'
          }}
        >
          TEMPLATE CHOOSER
        </Typography>
      </Box>

      {expanded && (
        <Paper
          elevation={12}
          component="aside"
          aria-label="Template chooser panel"
          sx={{
            width: { xs: 'min(368px, calc(100vw - 40px))', sm: EXPANDED_PANEL_W },
            maxHeight: 'calc(100vh - 88px)',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '0 12px 12px 0',
            overflow: 'hidden',
            border: '1px solid',
            borderLeft: 'none',
            borderColor: 'divider',
            ml: '-1px'
          }}
        >
          <Box sx={{ px: 1.5, py: 1.25, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <PaletteIcon color="primary" fontSize="small" />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                  Template chooser
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Saved in this browser
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', p: 1.5, bgcolor: 'background.default' }}>
            <SelectorSection
              icon={<PaletteIcon fontSize="small" />}
              title="Page atmosphere"
              subtitle="Full-page gradient"
              themeId={pageTemplateId}
              onSelect={setPageTemplateId}
              defaultExpanded
            />
            <SelectorSection
              icon={<ViewQuiltIcon fontSize="small" />}
              title="Hero & entry panels"
              subtitle="Get-started card & hero accents"
              themeId={panelsTemplateId}
              onSelect={setPanelsTemplateId}
            />
            <SelectorSection
              icon={<RouteIcon fontSize="small" />}
              title="Career journey"
              subtitle="360° wheel & 100-step path"
              themeId={careerTemplateId}
              onSelect={setCareerTemplateId}
            />
            <SelectorSection
              icon={<GridViewIcon fontSize="small" />}
              title="Features & CTA"
              subtitle="Capability cards & footer"
              themeId={featuresTemplateId}
              onSelect={setFeaturesTemplateId}
            />
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '12px !important',
                bgcolor: 'background.paper',
                '&:before': { display: 'none' }
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <NavigationIcon fontSize="small" color="primary" />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Top navigation
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Auto / Light / Dark bar
                    </Typography>
                  </Box>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1.5, pb: 1.5 }}>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  size="small"
                  value={navVariant}
                  onChange={(_, v) => v && setNavVariant(v)}
                >
                  <ToggleButton value="auto" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}>
                    Auto
                  </ToggleButton>
                  <ToggleButton value="light" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}>
                    Light
                  </ToggleButton>
                  <ToggleButton value="dark" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}>
                    Dark
                  </ToggleButton>
                </ToggleButtonGroup>
              </AccordionDetails>
            </Accordion>
          </Box>

          <Divider />
          <Stack spacing={1} sx={{ p: 1.5, bgcolor: 'background.paper' }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={resetAll}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Reset all
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<SyncIcon />}
              onClick={syncToQueryPortal}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Sync to query page
            </Button>
          </Stack>
        </Paper>
      )}
    </Box>
  );
}
